import React, {
  createRef,
  ReactNode,
  useCallback,
  useMemo,
  KeyboardEvent
} from 'react';
import styled from 'styled-components';
import { mq } from '~/lib/mq';

const Wrapper = styled.div`
  width: 100%;
`;

const Content = styled.div`
  color: ${({ theme }) => theme.colors.charcoal};
`;

const Nav = styled.ul`
  display: flex;
  width: 100%;
  margin-bottom: 1rem;
  position: relative;
`;

type NavItemProps = {
  isSelected: boolean;
};

const NavItem = styled.li<NavItemProps>`
  flex: 1;
  opacity: ${({ isSelected }) => (isSelected ? 1 : 0.5)};
`;

const Button = styled.button`
  border: none;
  background-color: inherit;
  color: inherit;
  margin: 0;
  padding: 0.5rem 0rem;
  width: 100%;

  text-align: center;
  color: ${({ theme }) => theme.colors.darkBlue};
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;

  ${mq('>small')`
    font-size: 1.125rem;
  `};
`;

type IndicatorProps = {
  index: number;
  count: number;
};

const Indicator = styled.div<IndicatorProps>`
  background-color: ${({ theme }) => theme.colors.darkBlue};
  height: 0.25rem;
  position: absolute;
  transition: left 0.3s;
  bottom: 0px;
  width: ${({ count }) => 100 / count}%;
  left: ${({ index, count }) => `${index * (100 / count)}%`};
  border-radius: 1rem;
`;

type Tab<T> = {
  value: T;
  label: string;
  content: ReactNode;
};
type Props<T> = {
  value: T;
  tabs: Tab<T>[];
  onChange: (value: T) => void;
};

export function Tabs<T>({ value, tabs, onChange }: Props<T>) {
  const tabsWithRef = useMemo(
    () =>
      tabs.map((tab) => ({
        ...tab,
        ref: createRef<HTMLButtonElement>()
      })),
    [tabs]
  );

  const handleKeyPress = useMemo(
    () => (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      const lastTabIndex = tabsWithRef.length - 1;
      const nextIndex = index < lastTabIndex ? index + 1 : 0;
      const prevIndex = index > 0 ? index - 1 : lastTabIndex;

      if (event.key === 'ArrowLeft') {
        tabsWithRef[prevIndex].ref.current?.focus();
      }

      if (event.key === 'ArrowRight') {
        tabsWithRef[nextIndex].ref.current?.focus();
      }
    },
    [tabsWithRef]
  );

  const renderTab = useCallback(
    (tab, index) => {
      const active = tab.value === value;

      return (
        <NavItem
          key={`${tab.value || tab.label}`}
          isSelected={active}
          role="presentation"
        >
          <Button
            type="button"
            ref={tab.ref}
            role="tab"
            tabIndex={active ? undefined : -1}
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            onKeyUp={(event) => handleKeyPress(event, index)}
          >
            {tab.label}
          </Button>
        </NavItem>
      );
    },
    [onChange, value, handleKeyPress]
  );

  const content = useMemo<ReactNode>(
    () => tabsWithRef.find((x) => x.value === value)?.content || null,
    [tabsWithRef, value]
  );

  const index = useMemo(() => tabs.map((x) => x.value).indexOf(value), [
    tabs,
    value
  ]);

  return (
    <Wrapper>
      <Nav role="tablist">
        {tabsWithRef.map(renderTab)}
        <Indicator count={tabs.length} index={index} />
      </Nav>
      <Content>{content}</Content>
    </Wrapper>
  );
}
